import React, { useContext, useEffect, useState } from "react";
import { ConfirmDialog, Dropdown, Text, DraggableCheckboxList, CheckboxListItem, depascalize } from "sprint-common-web";

import { OrderListingContext } from "../context/OrderListingContext";
import { ColumnConfig, OrderColumn, OrderListingConfig, getSortedOrderColumns } from "./OrderListingConfig";

interface SetOrderListingColumnsConfigDialogProps {
    isOpen: boolean;
    pageSize: number;
    setPageSize(pageSize: number): void;
    onClose(): void;
}

export const SetOrderListingColumnsConfigDialog: React.VFC<SetOrderListingColumnsConfigDialogProps> = (
    props: SetOrderListingColumnsConfigDialogProps
) => {
    const [items, setItems] = useState<CheckboxListItem[]>([]);
    const [pageSize, setPageSize] = useState<number>(props.pageSize);

    const { listingConfig, saveListingConfig } = useContext(OrderListingContext);

    useEffect(() => {
        const checkboxListItems = getCheckboxListItems(listingConfig);

        setItems(checkboxListItems);
    }, []);

    const handleConfirm = () => {
        props.setPageSize(pageSize);

        const config = mapToListingConfig(items, pageSize);

        saveListingConfig(config);

        props.onClose();
    };

    const handlePageSizeChange = (key: string) => {
        setPageSize(Number(key));
    };

    return (
        <ConfirmDialog
            title={"Listing configuration"}
            text={""}
            confirm={{
                onClick: handleConfirm
            }}
            cancel={{
                onClick: props.onClose
            }}
            width={700}
            contentHeight={400}
        >
            <div style={{ display: "flex" }}>
                <div style={{ width: "50%", float: "left", left: 0, paddingRight: 20 }}>
                    <Text value="Page size" styles={{ root: { paddingBottom: 10 } }} />
                    <Dropdown
                        options={pageSizeDropDownOptions.map((key) => ({ key, text: key }))}
                        selectedKey={String(pageSize)}
                        onChange={handlePageSizeChange}
                    />
                </div>
                <div style={{ width: "50%", float: "right" }}>
                    <Text value="Columns" styles={{ root: { paddingBottom: 10 } }} />
                    <DraggableCheckboxList items={items} setItems={setItems} />
                </div>
            </div>
        </ConfirmDialog>
    );
};

const pageSizeDropDownOptions: string[] = ["10", "20", "50", "100"];

const isItemDisabled = (key: OrderColumn) => {
    return key === OrderColumn.PoNumber || key === OrderColumn.Actions;
};

const getCheckboxListItems = (config: OrderListingConfig): CheckboxListItem[] => {
    return getSortedOrderColumns(config.columns).map(([key, column]) => {
        const resolvedKey = Number(key);

        return {
            key: resolvedKey,
            text: depascalize(OrderColumn[resolvedKey]),
            checked: column.visible,
            disabled: isItemDisabled(resolvedKey)
        };
    });
};

const mapToListingConfig = (values: CheckboxListItem[], pageSize: number): OrderListingConfig => {
    const columns = {} as Record<OrderColumn, ColumnConfig>;

    let order = 0;

    for (const item of values) {
        const index = Number(item.key) as OrderColumn;
        columns[index] = { order, visible: item.checked };

        order++;
    }

    return {
        pageSize,
        columns
    };
};
